import { ReactNode, useState } from "react";
import { createContext } from "use-context-selector";
import { api, wrapperRequests } from "../services/api";
import { routesURL } from "../services/routesUrl";
import { IOrders, OrderDetails } from "../@types/Orders";
import { OrderItemProcessStatus } from "../enums/Orders";
import { ToastPosition, toast } from "react-toastify";

interface OrdersContextContextType {
  cancelShipmentsItem: (itemId: string) => Promise<void>;
  cancelProductListLineItem: (
    orderID: string,
    orderLineID: string,
  ) => Promise<void>;
  fetchOrderDetails: (caOrderId: string) => Promise<void>;
  loading: {
    orderDetails: boolean;
    cancelItem: boolean;
    id: string;
  };
  ordersList: IOrders[];
  orderDetails: OrderDetails;
  ordersSyncErrorLoading: {
    loading: boolean;
    orderId: string;
  };
  ordersPagination: {
    rowsPerPage: number;
    page: number;
    totalDocuments: number;
    totalPages: number;
  };
  retryImport: (orderId: string, fromOrderDetails?: boolean) => Promise<void>;
  selectPage: (value: number) => void;
  selectRowsPerPage: (value: number) => void;
  toastify: (
    type: keyof Toastify,
    message: string,
    position: ToastPosition,
  ) => void;
}

interface Toastify {
  success: string;
  error: string;
}

interface OrdersProviderProps {
  children: ReactNode;
}

export const OrdersContext = createContext({} as OrdersContextContextType);

export function OrdersProvider({ children }: OrdersProviderProps) {
  const [ordersList, setOrdersList] = useState<IOrders[]>([]);
  const [ordersSyncErrorLoading, setOrdersSyncErrorLoading] = useState({
    loading: false,
    orderId: "",
  });
  const [ordersPagination, setOrdersPagination] = useState({
    rowsPerPage: 10,
    page: 1,
    totalDocuments: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState({
    orderDetails: false,
    cancelItem: false,
    id: "",
  });
  const [orderDetails, setOrderDetails] = useState<OrderDetails>(
    {} as OrderDetails,
  );

  const toastify = (
    type: keyof Toastify,
    message: string,
    position: ToastPosition,
  ) => {
    toast[type](message, {
      position,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const handleLoading = (loadingType: keyof typeof loading, value: boolean) => {
    setLoading((state) => ({
      ...state,
      [loadingType]: value,
    }));
  };

  const fetchOrderDetails = async (caOrderId: string) => {
    handleLoading("orderDetails", true);

    try {
      const { data } = await wrapperRequests(
        routesURL.report.orders.getSales(caOrderId),
        "GET",
      );

      setOrderDetails(data.order);
    } catch (error) {
      throw Error(String(error));
    } finally {
      handleLoading("orderDetails", false);
    }
  };

  const updateOrderDetailsFromBackend = async (caOrderId: string) => {
    const { data } = await wrapperRequests(
      routesURL.report.orders.getSales(caOrderId),
      "GET",
    );

    setOrderDetails(data.order);
  };

  const retryImport = async (orderId: string, fromOrderDetails?: boolean) => {
    setOrdersSyncErrorLoading({
      loading: true,
      orderId,
    });
    try {
      await wrapperRequests(
        routesURL.report.orders.retryImport(orderId),
        "PUT",
      );

      setOrdersSyncErrorLoading({
        loading: false,
        orderId: "",
      });

      if (fromOrderDetails) {
        setOrderDetails((currentState) => {
          return {
            ...currentState,
            forceReprocess: true,
          };
        });
        toastify("success", "🚴🏽‍♂️ Order synced", "top-center");
      }
    } catch (error) {
      setOrdersSyncErrorLoading({
        loading: false,
        orderId: "",
      });
      if (fromOrderDetails) {
        toastify("error", "Error syncing item", "top-center");
      }
      throw new Error(String(error));
    }
  };

  const cancelShipmentsItem = async (itemId: string) => {
    setLoading((state) => {
      return {
        ...state,
        cancelItem: true,
        id: itemId,
      };
    });
    try {
      await wrapperRequests(
        routesURL.report.orders.cancelItem(itemId),
        "PUT",
      );

      await updateOrderDetailsFromBackend(orderDetails.id);

      toastify("success", "Item successfully canceled", "top-center");
    } catch (error) {
      toastify("error", "Error canceling item", "top-center");
      throw Error(String(error));
    } finally {
      handleLoading("cancelItem", false);
    }
  };

  const cancelProductListLineItem = async (
    orderID: string,
    orderLineID: string,
  ) => {
    setLoading((state) => {
      return {
        ...state,
        cancelItem: true,
        id: orderLineID,
      };
    });
    try {
      await wrapperRequests(
        routesURL.report.orders.cancelLineItem(orderID, orderLineID),
        "PUT",
        {
          data: {
            accessToken: api.defaults.headers.common["Authorization"],
            newStatus: OrderItemProcessStatus.CANCELLED,
          },
        },
      );

      await updateOrderDetailsFromBackend(orderID);

      toastify("success", "Item successfully canceled", "top-center");
    } catch (error) {
      toastify("error", "Error canceling item", "top-center");
      throw Error(String(error));
    } finally {
      handleLoading("cancelItem", false);
    }
  };

  const selectPage = (value: number) => {
    setOrdersPagination((currentValue) => {
      return {
        ...currentValue,
        page: value,
      };
    });
  };

  const selectRowsPerPage = (value: number) => {
    setOrdersPagination((currentValue) => {
      return {
        ...currentValue,
        rowsPerPage: value,
      };
    });
  };

  return (
    <OrdersContext.Provider
      value={{
        cancelShipmentsItem,
        cancelProductListLineItem,
        fetchOrderDetails,
        loading,
        ordersList,
        orderDetails,
        ordersSyncErrorLoading,
        ordersPagination,
        retryImport,
        selectPage,
        selectRowsPerPage,
        toastify,
      }}
    >
      {children}
    </OrdersContext.Provider>
  );
}
